import type {
	CoursesCommunityCourseQueryDto,
	CoursesConfirmWebinarDto,
	CoursesCreateWebinarDto,
	CoursesUpdateWebinarDto,
} from '@sportspredict/shared';
import type { Wire } from '../../types/common.types';
import { coursesApi } from './courses-base.api';
import { invalidatesAfterDelete } from '../../utils/courses-errors.util';
import type {
	AdminListWebinarsInput,
	AdminListWebinarsOutput,
	CompleteWebinarInput,
	CompleteWebinarOutput,
	ConfirmWebinarInput,
	ConfirmWebinarOutput,
	CreateWebinarInput,
	CreateWebinarOutput,
	DeleteWebinarInput,
	DeleteWebinarOutput,
	ListMyUpcomingWebinarsOutput,
	ListWebinarsForCourseInput,
	ListWebinarsForCourseOutput,
	ToggleInterestInput,
	ToggleInterestOutput,
	UpdateWebinarInput,
	UpdateWebinarOutput,
} from '../../types/webinars.types';

const TAG = 'courses:webinars' as const;
const INVALIDATES = [TAG, 'courses:office-hours', 'courses:dashboard'] as const;

export const webinarsEndpoints = coursesApi.injectEndpoints({
	endpoints: (builder) => ({
		listWebinarsForCourse: builder.query<ListWebinarsForCourseOutput, ListWebinarsForCourseInput>({
			query: (params) => ({
				url: 'courses/webinars',
				params: params satisfies Wire<CoursesCommunityCourseQueryDto>,
			}),
			providesTags: [TAG],
		}),
		listMyUpcomingWebinars: builder.query<ListMyUpcomingWebinarsOutput, void>({
			query: () => ({ url: 'courses/webinars/upcoming' }),
			providesTags: [TAG],
		}),
		toggleInterest: builder.mutation<ToggleInterestOutput, ToggleInterestInput>({
			query: ({ webinarId }) => ({
				url: `courses/webinars/${encodeURIComponent(webinarId)}/interest`,
				method: 'POST',
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminListWebinars: builder.query<AdminListWebinarsOutput, AdminListWebinarsInput>({
			query: (params) => ({
				url: 'courses/webinars/admin',
				params: params satisfies Wire<CoursesCommunityCourseQueryDto>,
			}),
			providesTags: [TAG],
		}),
		createWebinar: builder.mutation<CreateWebinarOutput, CreateWebinarInput>({
			query: (body) => ({
				url: 'courses/webinars',
				method: 'POST',
				body: body satisfies Wire<CoursesCreateWebinarDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		updateWebinar: builder.mutation<UpdateWebinarOutput, UpdateWebinarInput>({
			query: ({ id, ...body }) => ({
				url: `courses/webinars/${encodeURIComponent(id)}`,
				method: 'PATCH',
				body: body satisfies Wire<CoursesUpdateWebinarDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		deleteWebinar: builder.mutation<DeleteWebinarOutput, DeleteWebinarInput>({
			query: ({ id }) => ({ url: `courses/webinars/${encodeURIComponent(id)}`, method: 'DELETE' }),
			invalidatesTags: invalidatesAfterDelete(INVALIDATES),
		}),
		completeWebinar: builder.mutation<CompleteWebinarOutput, CompleteWebinarInput>({
			query: ({ id }) => ({
				url: `courses/webinars/${encodeURIComponent(id)}/complete`,
				method: 'POST',
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		confirmWebinar: builder.mutation<ConfirmWebinarOutput, ConfirmWebinarInput>({
			query: ({ id, ...body }) => ({
				url: `courses/webinars/${encodeURIComponent(id)}/confirm`,
				method: 'POST',
				body: body satisfies Wire<CoursesConfirmWebinarDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
	}),
});

export const {
	useListWebinarsForCourseQuery,
	useLazyListWebinarsForCourseQuery,
	useListMyUpcomingWebinarsQuery,
	useLazyListMyUpcomingWebinarsQuery,
	useToggleInterestMutation,
	useAdminListWebinarsQuery,
	useLazyAdminListWebinarsQuery,
	useCreateWebinarMutation,
	useUpdateWebinarMutation,
	useDeleteWebinarMutation,
	useCompleteWebinarMutation,
	useConfirmWebinarMutation,
} = webinarsEndpoints;
